import { useSelector } from "react-redux";
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getResults, recomputeResult, publishExamResults, getTranscriptData } from "../../../services/ExamResultService";
import { getExams } from "../../../services/ExamService";
import { getStudentProfiles } from "../../../services/StudentProfileService";
import { getUsers } from "../../../services/UserService";
import DataTablePagination from "../common/DataTablePagination";
import { PERMISSIONS, usePermissions, canExportTranscripts } from "../../../utils/permissions";
import { useTranslation } from "react-i18next";
import { formatDuration, getLocale } from "../../../utils/i18nLabels";
import TranscriptPDF from "../Transcript/TranscriptPDF";
import { pdf } from "@react-pdf/renderer";


const Results = () => {
  const { t } = useTranslation();
  // usePermissions() applique la même règle que le backend : SUPER_ADMIN passe
  // toujours, les autres comptes doivent porter la permission. (Lire
  // seulement user.permissions excluait le SUPER_ADMIN, qui était alors traité
  // comme un étudiant : colonne « Étudiant » absente, profils non chargés.)
  const can = usePermissions();
  // Export du relevé de notes (PDF) : permission TRANSCRIPT_EXPORT_OWN / _ANY (scolarité, direction
  // des études, responsable des examens, étudiant concerné) - ni l'administrateur ni l'enseignant.
  const canDownloadTranscript = canExportTranscripts(useSelector((state) => state.auth.auth?.user));
  // Un compte staff a accès à la liste des profils étudiants, un compte STUDENT non.
  const isStaff = can("STUDENT_PROFILE_LIST");
  const canRecompute = can(PERMISSIONS.EXAM_RESULT_RECOMPUTE);
  const canPublish = can(PERMISSIONS.EXAM_RESULT_PUBLISH);
  const colCount = 7 + (isStaff ? 1 : 0) + (isStaff ? 1 : 0) + (canRecompute ? 1 : 0);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [users, setUsers] = useState([]);

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [examFilter, setExamFilter] = useState("");
  const [passedFilter, setPassedFilter] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [recomputingId, setRecomputingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [publishing, setPublishing] = useState(false);

  useEffect(() => {
    getExams({ page: 1, limit: 200 }).then((res) => setExams(res.data?.data || [])).catch(() => { });
    // Ces deux appels sont réservés ADMIN/TEACHER côté API : un compte
    // STUDENT n'en a pas besoin (il ne voit que ses propres résultats,
    // pas de colonne "Étudiant" à résoudre), donc on ne les déclenche pas
    // pour lui - ça évite des 403 inutiles.
    if (isStaff) {
      getStudentProfiles({ page: 1, limit: 200 }).then((res) => setStudents(res.data?.data || [])).catch(() => { });
      getUsers({ page: 1, limit: 200 }).then((res) => setUsers(res.data?.data || [])).catch(() => { });
    }
  }, [isStaff]);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params = { page, limit, sort_by: "corrected_at", sort_dir: "desc" };
      if (examFilter) params.exam_id = examFilter;
      if (passedFilter !== "") params.passed = passedFilter;

      const res = await getResults(params);
      const body = res.data;
      setResults(body.data || []);
      setTotal(body.pagination?.total ?? 0);
      setTotalPages(body.pagination?.totalPages ?? 1);
    } catch (err) {
      setLoadError(err?.response?.data?.message || t("pages.results.loadError"));
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [page, limit, examFilter, passedFilter, t]);

  useEffect(() => {
    fetchResults();
  }, [fetchResults]);

  useEffect(() => {
    if (!feedback) return undefined;
    const timer = setTimeout(() => setFeedback(null), 4000);
    return () => clearTimeout(timer);
  }, [feedback]);

  const examLabel = (id) => exams.find((e) => e.id === id)?.title || "-";
  const studentLabel = (id) => {
    const profile = students.find((s) => s.id === id);
    if (!profile) return "-";
    const user = users.find((u) => u.id === profile.user_id);
    return user ? `${user.email} (${profile.matricule})` : profile.matricule;
  };

  const handlePublish = async () => {
    if (!examFilter) {
      setFeedback({ type: "danger", message: t("pages.results.selectionnezExamenPourPublierSesResultats") });
      return;
    }
    setPublishing(true);
    try {
      const res = await publishExamResults(examFilter);
      setFeedback({ type: "success", message: res.data?.message || t("pages.results.resultatsPublies") });
      fetchResults();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.results.impossiblePublierResultatsExamen") });
    } finally {
      setPublishing(false);
    }
  };

  const handleRecompute = async (result) => {
    setRecomputingId(result.id);
    try {
      await recomputeResult(result.attempt_id);
      setFeedback({ type: "success", message: t("pages.results.recomputeSuccess") });
      fetchResults();
    } catch (err) {
      setFeedback({ type: "danger", message: err?.response?.data?.message || t("pages.results.recomputeError") });
    } finally {
      setRecomputingId(null);
    }
  };

  const handleDownloadTranscript = async (studentId) => {
    setDownloadingId(studentId);

    try {
      const response = await getTranscriptData(studentId, { purpose: "export" });

      const transcript = response.data?.data ?? response.data;

      if (!transcript) {
        throw new Error("Relevé introuvable.");
      }

      const blob = await pdf(
        <TranscriptPDF transcript={transcript} />
      ).toBlob();

      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = "releve-de-notes.pdf";

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Erreur génération relevé :", err);

      setFeedback({
        type: "danger",
        message: t("pages.results.impossibleGenererReleveNotes"),
      });
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">{t("menu.dashboard.title")}</Link>
                </li>
                <li className="breadcrumb-item active">{isStaff ? t("pages.results.breadcrumb") : t("pages.results.myResultsBreadcrumb")}</li>
              </ol>
            </nav>
          </div>
        </div>

        {feedback && (
          <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
            {feedback.message}
            <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
          </div>
        )}

        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between flex-wrap pb-0">
            <h4 className="mb-3">{isStaff ? t("menu.results.results") : t("pages.results.myResultsBreadcrumb")}</h4>
            <div className="d-flex align-items-center flex-wrap">
              <div className="mb-3 me-2">
                <select className="form-select" value={examFilter} onChange={(e) => { setExamFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("common.ui.tousExamens")}</option>
                  {exams.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.title}
                    </option>
                  ))}
                </select>
              </div>
              {/* <div className="mb-3 me-2">
                <select className="form-select" value={passedFilter} onChange={(e) => { setPassedFilter(e.target.value); setPage(1); }}>
                  <option value="">{t("pages.results.allResults")}</option>
                  <option value="true">{t("common.ui.admis")}</option>
                  <option value="false">{t("common.ui.nonAdmis")}</option>
                </select>
              </div> */}
              {canPublish && (
                <div className="mb-3">
                  <button
                    type="button"
                    className="btn btn-outline-success"
                    disabled={publishing || !examFilter}
                    title={!examFilter ? t("pages.results.selectionnezAbordExamen") : ""}
                    onClick={handlePublish}
                  >
                    <i className="ti ti-send me-1"></i>
                    {publishing ? t("pages.results.publication") : t("pages.results.publierResultatsExamen")}
                  </button>
                </div>
              )}
            </div>
          </div>

          <DataTablePagination
            variant="header"
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={(v) => { setLimit(v); setPage(1); }}
          />

          <div className="card-body p-0 py-3">
            <div className="custom-datatable-filter table-responsive">
              <table className="table datatable">
                <thead className="thead-light">
                  <tr>
                    {isStaff && <th>{t("roles.STUDENT")}</th>}
                    <th>{t("common.ui.examen")}</th>
                    <th>{t("common.fields.points")}</th>
                    <th>{t("common.ui.score")}</th>
                    <th>{t("common.ui.tempsPasse")}</th>
                    {/* <th>{t("common.ui.resultat")}</th> */}
                    <th>{t("common.ui.corrige")}</th>
                    {isStaff && <th>{t("pages.results.publicationColumn")}</th>}
                    <th>{t("pages.results.releve")}</th>
                    {canRecompute && <th>{t("common.crud.actionColumn")}</th>}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={colCount} className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">{t("common.crud.loading")}</span>
                        </div>
                      </td>
                    </tr>
                  ) : loadError ? (
                    <tr>
                      <td colSpan={colCount} className="text-center py-4 text-danger">
                        {loadError}
                      </td>
                    </tr>
                  ) : results.length > 0 ? (
                    results.map((result) => (
                      <tr key={result.id}>
                        {isStaff && <td>{studentLabel(result.student_id)}</td>}
                        <td>{examLabel(result.exam_id)}</td>
                        <td>
                          {result.total_points_earned} / {result.total_points_possible}
                        </td>
                        <td>{result.score_out_of_20 ?? "-"} / 20 <small className="text-muted">({result.score_percentage}%)</small></td>
                        <td>
                          {formatDuration(result.time_spent_seconds)}
                          {result.attempt_status === "timed_out" && (
                            <span className="badge badge-soft-warning ms-1">{t("pages.results.expired")}</span>
                          )}
                        </td>
                        {/* <td>
                          <span className={`badge badge-soft-${result.passed ? "success" : "danger"}`}>
                            {result.passed ? t("common.ui.admis") : t("common.ui.nonAdmis")}
                          </span>
                        </td> */}
                        <td>{result.corrected_at ? new Date(result.corrected_at).toLocaleString(getLocale()) : "-"}</td>
                        {isStaff && (
                          <td>
                            <span className={`badge badge-soft-${result.publication_status === "published" ? "success" : "warning"}`}>
                              {result.publication_status === "published" ? t("pages.results.published") : t("pages.results.provisional")}
                            </span>
                          </td>
                        )}
                        <td>
                          {canDownloadTranscript && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              disabled={downloadingId === result.student_id}
                              onClick={() => handleDownloadTranscript(result.student_id)}
                            >
                              <i className="ti ti-file-download me-1"></i>
                              {downloadingId === result.student_id ? "..." : "PDF"}
                            </button>
                          )}
                        </td>
                        {canRecompute && (
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleRecompute(result)}
                              disabled={recomputingId === result.id}
                            >
                              <i className="ti ti-refresh me-1"></i>
                              {recomputingId === result.id ? t("pages.results.recomputing") : t("pages.results.recompute")}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={colCount} className="text-center py-4">
                        <i className="ti ti-search-off fs-1 text-muted"></i>
                        <h5 className="mt-2">{t("pages.results.noResultsFound")}</h5>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <DataTablePagination
              variant="footer"
              page={page}
              limit={limit}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onLimitChange={(v) => { setLimit(v); setPage(1); }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Results;